import {
  IDADE_ATRASADO_SEGUNDOS,
  IDADE_OBSOLETO_SEGUNDOS,
  SEVERIDADE_ESTADO,
} from '@/constants/status.constants';
import type {
  EstadoServico,
  FrescorDados,
  ItemResumo,
  Manutencao,
  Servico,
  StatusPublico,
} from '@/types/status.types';
import { formatarDataHoraBR, formatarPercentual } from './formatacao.helpers';

/** Idade do snapshot em segundos. Datas inválidas contam como infinitamente velhas. */
export function calcularIdadeSegundos(geradoEm: string | null | undefined, agora = Date.now()) {
  if (!geradoEm) {
    return Number.POSITIVE_INFINITY;
  }

  const momento = new Date(geradoEm).getTime();

  if (Number.isNaN(momento)) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.max(0, Math.floor((agora - momento) / 1000));
}

export function classificarFrescor(idadeSegundos: number): FrescorDados {
  if (idadeSegundos >= IDADE_OBSOLETO_SEGUNDOS) {
    return 'obsoleto';
  }

  if (idadeSegundos >= IDADE_ATRASADO_SEGUNDOS) {
    return 'atrasado';
  }

  return 'atual';
}

/**
 * Estado de um serviço já considerando o frescor do snapshot.
 *
 * Com dado obsoleto, nenhuma afirmação sobre saúde se sustenta — inclusive a de que
 * está tudo bem. Por isso tudo vira "desconhecido" em vez de continuar verde.
 */
export function estadoEfetivo(estado: EstadoServico, frescor: FrescorDados): EstadoServico {
  return frescor === 'obsoleto' ? 'desconhecido' : estado;
}

/**
 * Estado geral a partir dos serviços: prevalece o mais grave.
 *
 * O campo `estado_geral` do contrato é a fonte primária — o Nicon conhece a
 * criticidade de cada componente, e um relatório secundário fora do ar não deve
 * pintar a página inteira de vermelho. A derivação aqui existe como rede de segurança
 * para snapshots incoerentes.
 */
export function derivarEstadoGeral(status: StatusPublico, frescor: FrescorDados): EstadoServico {
  if (frescor === 'obsoleto') {
    return 'desconhecido';
  }

  if (status.servicos.length === 0) {
    return status.estado_geral;
  }

  const maisGrave = status.servicos.reduce<EstadoServico>((pior, servico) => {
    return SEVERIDADE_ESTADO[servico.estado] > SEVERIDADE_ESTADO[pior] ? servico.estado : pior;
  }, 'operacional');

  return SEVERIDADE_ESTADO[maisGrave] > SEVERIDADE_ESTADO[status.estado_geral]
    ? maisGrave
    : status.estado_geral;
}

export function contarServicosAfetados(servicos: Servico[]): number {
  return servicos.filter((servico) => servico.estado !== 'operacional').length;
}

export function calcularDisponibilidadeMedia(servicos: Servico[]): number | null {
  const validos = servicos.filter((servico) => Number.isFinite(servico.disponibilidade));

  if (validos.length === 0) {
    return null;
  }

  const soma = validos.reduce((total, servico) => total + servico.disponibilidade, 0);

  return soma / validos.length;
}

/** Próxima manutenção que ainda não terminou, se houver. */
export function proximaManutencao(
  manutencoes: Manutencao[],
  agora = Date.now(),
): Manutencao | null {
  const futuras = manutencoes
    .filter((manutencao) => {
      const fim = new Date(manutencao.fim).getTime();

      return !Number.isNaN(fim) && fim >= agora;
    })
    .sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());

  return futuras[0] ?? null;
}

/**
 * Faixa de números do cartão principal.
 *
 * Com snapshot obsoleto, os três valores viram travessão: repetir "99,98%" sem saber
 * de quando é o dado transformaria uma métrica em uma afirmação falsa.
 */
export function montarResumoGeral(status: StatusPublico, frescor: FrescorDados): ItemResumo[] {
  const obsoleto = frescor === 'obsoleto';
  const afetados = contarServicosAfetados(status.servicos);
  const media = calcularDisponibilidadeMedia(status.servicos);
  const proxima = proximaManutencao(status.manutencoes);

  return [
    {
      rotulo: 'Serviços afetados',
      valor: obsoleto
        ? '—'
        : afetados === 0
          ? 'Nenhum'
          : `${afetados} de ${status.servicos.length}`,
    },
    {
      rotulo: `Disponibilidade (${status.janela_dias} dias)`,
      valor: obsoleto || media === null ? '—' : formatarPercentual(media),
    },
    {
      rotulo: 'Próxima manutenção',
      valor: proxima ? formatarDataHoraBR(proxima.inicio) : 'Nenhuma prevista',
    },
  ];
}
